import type { QariHifzStatus, QariProfileDraft } from '@/types/qari';
import {
  QariFormField,
  QariTextInput,
} from '@/components/qari/QariFormField';
import { TagInput } from '@/components/qari/TagInput';
import { cn } from '@/lib/utils/cn';

type Props = {
  value: Partial<QariProfileDraft>;
  onChange: (patch: Partial<QariProfileDraft>) => void;
  disabled?: boolean;
};

const HIFZ_OPTIONS: { v: QariHifzStatus; label: string }[] = [
  { v: 'full', label: 'Full hifz' },
  { v: 'partial', label: 'Partial' },
  { v: 'none', label: 'No hifz' },
];

export function StepQualifications({ value, onChange, disabled }: Props) {
  return (
    <div className="space-y-6">
      <QariFormField
        label="Years of experience"
        htmlFor="qari-years"
        hint="Years actively teaching or reciting."
      >
        <QariTextInput
          id="qari-years"
          type="number"
          value={value.years_experience?.toString() ?? ''}
          onChange={(v) => {
            const n = v === '' ? null : Number(v);
            onChange({
              years_experience: Number.isFinite(n) && n !== null ? n : null,
            });
          }}
          disabled={disabled}
          placeholder="0"
          className="max-w-[10rem]"
        />
      </QariFormField>

      <QariFormField
        label="Qira'at"
        hint="Recitation styles you teach (e.g. Hafs an Asim, Warsh an Nafi)."
      >
        <TagInput
          value={value.qiraat ?? []}
          onChange={(v) => onChange({ qiraat: v })}
          placeholder="Add a qira'at and press Enter"
          disabled={disabled}
        />
      </QariFormField>

      <QariFormField
        label="Specializations"
        hint="Tajweed, hifz, ijazah issuance, kids, adults, etc."
      >
        <TagInput
          value={value.specializations ?? []}
          onChange={(v) => onChange({ specializations: v })}
          placeholder="e.g. Tajweed, Hifz"
          disabled={disabled}
        />
      </QariFormField>

      <QariFormField label="Hifz status">
        <div
          role="radiogroup"
          aria-label="Hifz status"
          className="inline-flex p-1 rounded-full bg-ink-100 dark:bg-ink-900/70 border border-ink-200/70 dark:border-ink-800/70"
        >
          {HIFZ_OPTIONS.map((opt) => {
            const active = value.hifz_status === opt.v;
            return (
              <button
                key={opt.v}
                role="radio"
                aria-checked={active}
                type="button"
                disabled={disabled}
                onClick={() => onChange({ hifz_status: opt.v })}
                className={cn(
                  'px-3 h-8 rounded-full text-xs font-medium transition-colors',
                  active
                    ? 'bg-accent text-ink-50'
                    : 'text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-ink-50',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </QariFormField>

      <div className="grid sm:grid-cols-2 gap-6">
        <QariFormField
          label="Contact email"
          htmlFor="qari-contact-email"
          hint="Optional. Visible only once approved."
        >
          <QariTextInput
            id="qari-contact-email"
            type="email"
            value={value.contact_email ?? ''}
            onChange={(v) => onChange({ contact_email: v })}
            maxLength={120}
            disabled={disabled}
            placeholder="name@example.com"
          />
        </QariFormField>
        <QariFormField
          label="Contact phone"
          htmlFor="qari-contact-phone"
          hint="Optional. Visible only once approved."
        >
          <QariTextInput
            id="qari-contact-phone"
            type="tel"
            value={value.contact_phone ?? ''}
            onChange={(v) => onChange({ contact_phone: v })}
            maxLength={32}
            disabled={disabled}
            placeholder="+92 300 0000000"
          />
        </QariFormField>
      </div>
    </div>
  );
}
